import type { PlayerPublic, RoomView } from "@shared/types";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState, type ReactNode } from "react";
import { Avatar, type Mood } from "../../components/Avatar";
import { Btn } from "../../components/Button";
import { Confetti } from "../../components/Confetti";
import { Check, Home, LogOut, Pause, Play, RotateCcw, SkipForward } from "../../components/Icon";
import { Sheet } from "../../components/Sheet";
import { useI18n } from "../../i18n";
import { asset } from "../../lib/asset";
import { avatarVoice, play } from "../../lib/sfx";
import type { RoomClient } from "../../lib/room-client";
import { gameMeta } from "../meta";
import { useAct } from "./useRoom";

/** Top bar during a game: game name, code, pause/skip for the host. */
export function GameChrome({ client, view, onSkip, skipLabel, extra }: { client: RoomClient; view: RoomView; onSkip?(): void; skipLabel?: string; extra?: ReactNode }) {
  const { d } = useI18n();
  const act = useAct(client);
  const g = gameMeta(view.game);
  const isHost = !!view.me?.isHost;
  const [menu, setMenu] = useState(false);
  return (
    <div className="shell flex h-14 items-center gap-3">
      <span className="display truncate text-xl" style={{ color: g.color }}>{d.games[view.game].name}</span>
      {!view.local && <span className="tabular rounded-full bg-ground-3 px-3 py-1 text-sm font-black tracking-widest">{view.code}</span>}
      <div className="ml-auto flex items-center gap-2">
        {extra}
        {isHost && onSkip && (
          <button className="chip" onClick={onSkip} aria-label={skipLabel ?? d.room.skip}>
            <SkipForward size={17} /> <span className="hidden sm:inline">{skipLabel ?? d.room.skip}</span>
          </button>
        )}
        {isHost && (
          <button className="icon-btn" aria-label={view.paused ? d.room.resume : d.room.pause} onClick={() => act({ t: view.paused ? "resume" : "pause" })}>
            {view.paused ? <Play size={20} /> : <Pause size={20} />}
          </button>
        )}
        <button className="icon-btn" aria-label={d.nav.menu} onClick={() => setMenu(true)}>
          <LogOut size={19} />
        </button>
      </div>
      <Sheet open={menu} onClose={() => setMenu(false)} title={d.room.hostControls} size="narrow">
        <div className="grid gap-3">
          {isHost && (
            <Btn variant="soft" block icon={<RotateCcw size={18} />} onClick={() => { setMenu(false); act({ t: "lobby" }); }}>
              {d.room.backToLobby}
            </Btn>
          )}
          <Btn variant="soft" block icon={<Home size={18} />} onClick={() => location.assign(asset(""))}>
            {d.room.toHome}
          </Btn>
          {!view.local && (
            <Btn variant="ghost" block icon={<LogOut size={18} />} onClick={async () => { await act({ t: "leave" }, true); location.assign(asset("")); }}>
              {d.lobby.leave}
            </Btn>
          )}
        </div>
      </Sheet>
    </div>
  );
}

/** Row of player avatars with a done-check (submitted / voted / answered). */
export function PlayerStrip({ players, done, highlight, size = 46, moods }: { players: PlayerPublic[]; done?: string[]; highlight?: string | null; size?: number; moods?: Record<string, Mood> }) {
  return (
    <ul className="flex flex-wrap justify-center gap-2.5">
      {players.map((p) => {
        const ok = done?.includes(p.id);
        return (
          <li key={p.id} className="relative" title={p.name}>
            <motion.div animate={{ scale: highlight === p.id ? 1.15 : 1, y: ok ? -3 : 0 }} transition={{ type: "spring", stiffness: 500, damping: 22 }}>
              <Avatar index={p.avatar} color={p.color} size={size} mood={moods?.[p.id] ?? (highlight === p.id ? "talk" : "still")} dim={done ? !ok : false} />
            </motion.div>
            {ok && (
              <span className="absolute -right-1 -bottom-1 grid h-5 w-5 place-items-center rounded-full bg-good text-[#05271a]">
                <Check size={13} strokeWidth={3} />
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export interface ScoreRow {
  id: string;
  score: number;
  delta?: number;
  sub?: ReactNode;
}

/** Animated leaderboard: rows glide into their new places, deltas count up. */
export function Scoreboard({ view, rows, title, moodByRank }: { view: RoomView; rows: ScoreRow[]; title?: ReactNode; moodByRank?: boolean }) {
  const { d } = useI18n();
  const sorted = [...rows].sort((a, b) => b.score - a.score);
  const top = sorted[0]?.score ?? 0;
  return (
    <div className="space-y-3">
      {title && <h2 className="display text-3xl sm:text-4xl">{title}</h2>}
      <ol className="space-y-2.5">
        <AnimatePresence initial={false}>
          {sorted.map((r, i) => {
            const p = view.players.find((x) => x.id === r.id);
            if (!p) return null;
            const leader = i === 0 && top > 0;
            const mood: Mood = moodByRank ? (leader ? "happy" : i === sorted.length - 1 && sorted.length > 1 ? "sad" : "still") : "still";
            return (
              <motion.li
                key={r.id}
                layout
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ type: "spring", stiffness: 380, damping: 32 }}
                className="flex items-center gap-3 rounded-[18px] px-3 py-2.5"
                style={{ background: leader ? "color-mix(in oklab, var(--color-flame) 22%, var(--color-ground-3))" : "var(--color-ground-3)" }}
              >
                <span className="tabular w-6 text-center text-lg font-black text-ink-3">{i + 1}</span>
                <Avatar index={p.avatar} color={p.color} size={48} mood={mood} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-lg font-extrabold">
                    {p.name}
                    {p.id === view.me?.id && <span className="ml-2 text-sm text-ink-3">{d.common.you}</span>}
                  </span>
                  {r.sub && <span className="block truncate text-sm text-ink-3">{r.sub}</span>}
                </span>
                {r.delta ? (
                  <motion.span initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="tabular font-black text-good">
                    +{r.delta}
                  </motion.span>
                ) : null}
                <CountUp value={r.score} className="tabular min-w-[4ch] text-right text-2xl font-black" />
              </motion.li>
            );
          })}
        </AnimatePresence>
      </ol>
    </div>
  );
}

export function CountUp({ value, className = "" }: { value: number; className?: string }) {
  const [shown, setShown] = useState(value);
  useEffect(() => {
    const from = shown;
    if (from === value) return;
    const start = performance.now();
    const dur = 900;
    let raf = 0;
    const step = (t: number) => {
      const k = Math.min(1, (t - start) / dur);
      const e = 1 - Math.pow(1 - k, 3);
      setShown(Math.round(from + (value - from) * e));
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  return <span className={className}>{shown.toLocaleString("en-US").replace(/,/g, " ")}</span>;
}

/** End of game: podium with a jumping winner and teary losers, confetti, play-again. */
export function FinalResults({
  client, view, rows, title, children,
}: {
  client: RoomClient;
  view: RoomView;
  rows: ScoreRow[];
  title?: ReactNode;
  children?: ReactNode;
}) {
  const { d } = useI18n();
  const act = useAct(client);
  const sorted = [...rows].sort((a, b) => b.score - a.score);
  const best = sorted[0]?.score ?? 0;
  const winners = sorted.filter((r) => r.score === best && best > 0).map((r) => r.id);
  const isHost = !!view.me?.isHost;
  const iWon = !!view.me && winners.includes(view.me.id);
  useEffect(() => {
    const p = view.players.find((x) => x.id === winners[0]);
    play("win");
    if (p) avatarVoice(p.avatar, { delay: 2.2 });
    if (view.me && !iWon && view.me.role === "player") play("sad", { delay: 2.8, volume: 0.6 });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const podium = sorted.slice(0, 3);
  const order = podium.length === 3 ? [podium[1], podium[0], podium[2]] : podium;
  return (
    <div className="shell space-y-8 py-6">
      <Confetti fire={1} />
      <h1 className="display text-center text-4xl sm:text-5xl">{title ?? (winners.length > 1 ? d.room.winners : d.room.winner)}</h1>
      <div className="flex items-end justify-center gap-3 sm:gap-6">
        {order.map((r) => {
          const p = view.players.find((x) => x.id === r.id);
          if (!p) return null;
          const rank = sorted.indexOf(r);
          const h = rank === 0 ? 150 : rank === 1 ? 110 : 84;
          const win = winners.includes(r.id);
          return (
            <motion.div
              key={r.id}
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 + (2 - rank) * 0.25, type: "spring", stiffness: 260, damping: 20 }}
              className="flex w-[30%] max-w-[180px] flex-col items-center"
            >
              <Avatar index={p.avatar} color={p.color} size={rank === 0 ? 116 : 88} mood={win ? "happy" : "sad"} />
              <span className="mt-2 w-full truncate text-center text-lg font-extrabold">{p.name}</span>
              <CountUp value={r.score} className="tabular text-xl font-black text-flame" />
              <div
                className="mt-2 w-full rounded-t-[18px]"
                style={{ height: h, background: rank === 0 ? "var(--color-flame)" : rank === 1 ? "var(--color-ground-5)" : "var(--color-ground-4)" }}
              >
                <span className="display block pt-2 text-center text-3xl" style={{ color: rank === 0 ? "#2a1200" : "var(--color-ink)" }}>{rank + 1}</span>
              </div>
            </motion.div>
          );
        })}
      </div>
      {children}
      {sorted.length > 3 && <Scoreboard view={view} rows={sorted.slice(3)} />}
      <div className="flex flex-col justify-center gap-3 sm:flex-row">
        {isHost ? (
          <>
            <Btn size="lg" onClick={() => act({ t: "again" })} icon={<RotateCcw size={20} />}>{d.room.playAgain}</Btn>
            <Btn size="lg" variant="soft" onClick={() => act({ t: "lobby" })}>{d.room.backToLobby}</Btn>
          </>
        ) : (
          <p className="text-center font-bold text-ink-2">{d.lobby.waitingHost}</p>
        )}
      </div>
    </div>
  );
}

/** Big centred banner used for phase intros. */
export function Banner({ title, sub, color }: { title: ReactNode; sub?: ReactNode; color?: string }) {
  return (
    <motion.div
      initial={{ scale: 0.6, opacity: 0, rotate: -4 }}
      animate={{ scale: 1, opacity: 1, rotate: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 16 }}
      className="py-10 text-center"
    >
      <p className="display text-[clamp(44px,10vw,110px)]" style={{ color: color ?? "var(--color-ink)" }}>{title}</p>
      {sub && <p className="mt-3 text-2xl font-extrabold text-ink-2">{sub}</p>}
    </motion.div>
  );
}

export function AudienceNote() {
  const { d } = useI18n();
  return <p className="rounded-full bg-ground-3 px-4 py-2 text-center font-bold text-ink-2">{d.room.audienceNote}</p>;
}
